<template>
  <AutoDialogDrawer placement="centre" modal dismissible @close="$emit('close')">
    <template #title>{{ translate('Remove Domain Name') }}</template>
    <p>
      {{ translate('Are you sure you want to unregister this domain?') }}
      <strong class="text-nowrap">{{ domain.domain }}</strong>
    </p>
    <p>
      {{
        translate(
          "If you later decide to add the domain name back, you'll have to go through the domain verification process again.",
        )
      }}
    </p>
    <div v-if="requireLastDeleteConfirmation">
      <p>
        {{
          translate(
            'Since this is your last domain, removing it will disable the active login policy. Please acknowledge this before continuing.',
          )
        }}
      </p>
      <BootstrapCheck
        id="acknowledgedLastDelete"
        v-model="acknowledgedLastDelete"
        :label="
          translate('I understand that by removing this domain I\'m deactivating the login policy.')
        "
        allow-wrap
      />
    </div>
    <template #buttons>
      <BootstrapButton
        name="cancel"
        variant="secondary"
        :disabled="isDeleting"
        @click="$emit('close')"
      >
        {{ translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton name="remove" variant="danger" :disabled="deleteDisabled" @click="doDelete">
        {{ translate('Remove') }}
      </BootstrapButton>
    </template>
  </AutoDialogDrawer>
</template>

<script setup lang="ts">
import {Domain} from '@/api/apolloPlatform/loginPolicy';
import {useLocalisation} from '@/composables/useLocalisation';

const {translate} = useLocalisation();

const loginPolicyStore = useLoginPolicyStore();

const props = defineProps<{domain: Domain}>();
defineEmits<{close: []}>();

const isLastDomain = computed(
  () => loginPolicyStore.domains.length === 1 && loginPolicyStore.domains[0].id === props.domain.id,
);

const requireLastDeleteConfirmation = computed(
  () => isLastDomain.value && !!loginPolicyStore.loadedActivePolicy(),
);

const acknowledgedLastDelete = ref(false);

const isDeleting = ref(false);

const deleteDisabled = computed(
  () => isDeleting.value || (requireLastDeleteConfirmation.value && !acknowledgedLastDelete.value),
);

async function doDelete() {
  isDeleting.value = true;
  try {
    if (acknowledgedLastDelete.value) {
      const policy = loginPolicyStore.loadedActivePolicy();
      if (policy) {
        await loginPolicyStore.deactivatePolicy(policy);
      }
    }
    await loginPolicyStore.removeDomain(props.domain.id);
  } finally {
    isDeleting.value = false;
  }
}
</script>
